import { Immutable } from "immer";

import { IBasket, IBasketLine } from "../../models/catalogue";
import { IProductID } from "../../models/nominals";
import { IRootState } from "../reducers.interfaces";

/**
 * Basket reads for callers outside the checkout app.
 *
 * The lines live in checkout's slice because that is what loads and mutates
 * them, but "is this product in the basket, and how many" is a question any
 * app can ask -- the same reason the basket line thunks sit here rather than
 * in `apps/checkout`. Published so a caller does not have to know the shape
 * of another app's state to answer it.
 */

export const selectBasket = (
    state: Immutable<Pick<IRootState, "checkout">>,
): Immutable<IBasket> | null => state.checkout?.data.basket ?? null;

/** Whether the basket has been loaded at all, even if the answer was "none". */
export const selectBasketLoaded = (
    state: Immutable<Pick<IRootState, "checkout">>,
): boolean => state.checkout.data.basket_loaded;

/**
 * The line holding a given product among some basket lines, if any.
 *
 * Matched on the product's own id, so in a grid served one card per variant
 * each card finds its own line: a parent and its children are different
 * products, and Oscar records the child on the line.
 *
 * Parent-less lines only, matching the `!line.parent` filter the rest of the
 * basket UI applies before treating a line as the shopper's to manage --
 * `BasketLines` and `BasketSummary` both do it. A variant that arrived as a
 * bundle or gift child belongs to the line above it, and offering to remove
 * it here would undo something the basket page deliberately does not let a
 * shopper undo directly.
 *
 * A product can still hold more than one bare line -- Oscar splits them when
 * their options or stockrecords differ -- and this answers with the first.
 * Callers that only ever add a bare product will not produce a second.
 *
 * Takes lines rather than state so a caller holding a basket the store has
 * not seen yet -- the one a mutation thunk just returned, say -- asks the same
 * question the same way. One definition of "the shopper's line for this
 * product", so a render path and an analytics path cannot come to different
 * answers.
 */
export const findBasketLineForProduct = (
    lines: readonly Immutable<IBasketLine>[],
    productID: IProductID,
): Immutable<IBasketLine> | null =>
    lines.find((line) => !line.parent && line.product.id === productID) ?? null;

/** :func:`findBasketLineForProduct`, against the basket in the store. */
export const selectBasketLineForProduct = (
    state: Immutable<IRootState>,
    productID: IProductID,
): Immutable<IBasketLine> | null =>
    findBasketLineForProduct(selectBasket(state)?.lines ?? [], productID);
